import { useMemo } from "react";

import { QuickFilterBar as FilterBar } from "components/form_components/QuickFilterBar";
import { useNotices } from "lib/hooks/api/notices/useNotices";
import { EventDataTypes } from "lib/tracking";
import {
  cpvCodeFilterToLabel,
  keywordsFilterToLabel,
  supplierGuidsFilterToLabel,
  supplierMentionTypeToLabel,
} from "lib/utils/filterToLabelUtils";
import { DEFAULT_PAGINATION } from "./NoticeTable";
import {
  convertNoticeFiltersToSearchRequest,
  DEFAULT_NOTICE_SORT_ORDER,
  FilterSection,
  getAppliedFilters,
  getNoticeColumnCopy,
  NoticeFilters,
} from "./utils";

const sectionToFilterKey: Record<FilterSection, (keyof NoticeFilters)[]> = {
  suppliers: ["supplierGuids", "supplierSme", "supplierMentionType"],
  buyers: ["buyerListIds", "buyers", "buyerCategories", "buyerCountryRegions"],
  frameworks: ["frameworkIds", "frameworkFilterOptions"],
};

type Props = {
  filters: NoticeFilters;
  onClearFilter: (value: keyof NoticeFilters) => void;
  hiddenSections?: FilterSection[];
  hiddenFields?: (keyof NoticeFilters)[];
  onFilterClick: (key: keyof NoticeFilters) => void;
  clearAllFilters: () => void;
  clearAllFiltersHide?: () => boolean;
};
export function QuickFilterBar({
  filters,
  onClearFilter,
  hiddenFields = [],
  hiddenSections,
  onFilterClick,
  clearAllFilters,
  clearAllFiltersHide,
}: Props) {
  const { isLoading, data: noticeData } = useNotices(
    convertNoticeFiltersToSearchRequest(filters, DEFAULT_PAGINATION),
    false,
  );

  const totalResults = noticeData?.pagingInfo?.totalResults;
  const appliedFilters = getAppliedFilters(filters);

  const hideFields: (keyof NoticeFilters)[] = useMemo(() => {
    const hiddenSectionFields =
      hiddenSections?.map((section) => sectionToFilterKey[section]).flat() || [];
    const combinedHiddenFields = hiddenSectionFields.concat(hiddenFields);

    if (supplierMentionTypeToLabel(filters.supplierMentionType) === "") {
      combinedHiddenFields.push("supplierMentionType");
    }

    return combinedHiddenFields;
  }, [hiddenFields, filters.supplierMentionType, hiddenSections]);

  const filterKeyToLabel: Record<keyof NoticeFilters, string | undefined> = {
    signals: "Signals",
    assignee: "Assignee",
    buyerListIds: "Buyer lists",
    buyers: `Buyers (${filters.buyers.length})`,
    buyerCategories: "Buyer type",
    value: "Value",
    text: "Keywords",
    stage: "Stage",
    awardDate: "Award date",
    closeDate: "Close date",
    expiryDate: "Expiry date",
    relevanceScore: "Signal score",
    publishDate: "Publish date",
    suppliers: undefined,
    supplierGuids: supplierGuidsFilterToLabel(filters.supplierGuids),
    supplierSme: "Supplier SME",
    buyerCountryRegions: "Buyer location",
    noticeLists: "Notice lists",
    buyerIds: undefined,
    noticeIds: undefined,
    sort: undefined,
    frameworkIds: "Frameworks",
    frameworkFilterOptions: "Framework activity",
    supplierMentionType: supplierMentionTypeToLabel(filters.supplierMentionType),
    keywords: keywordsFilterToLabel(filters.keywords),
    excludeKeywords: `Exclude keywords (${filters.excludeKeywords.length})`,
    cpvCodes: cpvCodeFilterToLabel(filters.cpvCodes),
    includeHiddenNotices: "Include hidden notices",
  };

  return (
    <FilterBar<NoticeFilters>
      appliedFilters={appliedFilters}
      filterKeyToLabel={filterKeyToLabel}
      sortField={getNoticeColumnCopy(filters.sort?.field || DEFAULT_NOTICE_SORT_ORDER.field)}
      onClear={onClearFilter}
      isLoading={isLoading}
      totalResults={totalResults || 0}
      hiddenFields={[...hideFields, "text"]}
      dataType={EventDataTypes.record}
      onFilterClick={onFilterClick}
      clearAllFilters={clearAllFilters}
      clearAllFiltersHide={clearAllFiltersHide}
    />
  );
}
